import React, { useCallback } from 'react';

import Avatar from 'Shared/components/Avatar/Avatar';
import GoogleCalendarLogo from 'Shared/components/Logos/GoogleCalendarLogo';
import Outlook365Logo from 'Shared/components/Logos/Outlook365Logo';
import StyledCheckbox from 'Shared/components/Checkboxes/StyledCheckbox/StyledCheckbox';
import { ParamsForCreateWorkspace, WorkspaceType, RecurringMeetingSource } from 'Shared/constants/types';
import { sanitizeFullName } from 'Shared/helpers';

import AdditionalOptionsSection from './AdditionalOptionsSection/AdditionalOptionsSection';
import './RecurringMeetingItem.scss';

type Props = {
  type: WorkspaceType;
  currentUserEmail?: string;
  paramsForCreateWorkspace: ParamsForCreateWorkspace;
  onParamsForCreateWorkspaceUpdate: Function;
};

function RecurringMeetingItem({
  type,
  currentUserEmail,
  paramsForCreateWorkspace: {
    selected,
    uuid,
    teammateEmail,
    name,
    displayName,
    attendees,
    teammateAvatar,
    inviteAttendees,
    personalMessage,
    source,
  },
  onParamsForCreateWorkspaceUpdate,
}: Props) {
  const handleSelectMeeting = useCallback(() => onParamsForCreateWorkspaceUpdate(uuid, { selected: !selected }), [
    selected,
    onParamsForCreateWorkspaceUpdate,
  ]);

  const isTeammateType = type === WorkspaceType.isTeammate;

  const getInfoWhoAttendees = useCallback(() => {
    const attendeesForLabel = attendees.filter(({ email }) => email !== currentUserEmail);
    if (attendees.find(({ email }) => email === currentUserEmail))
      attendeesForLabel.unshift({ email: currentUserEmail, fullName: 'You', displayName: 'You' });

    return `Attendees: ${attendeesForLabel
      .map(({ email, fullName }) => sanitizeFullName(fullName) || email)
      .join(' & ')}`;
  }, [attendees, currentUserEmail]);

  return (
    <div className="recurring-meeting-item border cursor-pointer pl-2 pr-3 mb-2">
      <div className="d-flex" onClick={handleSelectMeeting}>
        <StyledCheckbox
          id={`recurring-meeting-item-${uuid}`}
          name="recurringMeetingItem"
          className="recurring-meeting-item-checkbox ml-1"
          checked={selected}
          onChange={handleSelectMeeting}
        />
        <div className="ml-2 font-weight-600">
          {displayName || name}
          <div className="recurring-meeting-item-attendees text-muted">
            {isTeammateType && source === RecurringMeetingSource.member && teammateEmail}
            {isTeammateType &&
              source === RecurringMeetingSource.groupMeeting &&
              'Found in your calendar (group meeting)'}
            {isTeammateType &&
              source !== RecurringMeetingSource.member &&
              source !== RecurringMeetingSource.groupMeeting &&
              'Found in your calendar'}
            {!isTeammateType && getInfoWhoAttendees()}
          </div>
        </div>
        {!isTeammateType && source === RecurringMeetingSource.google && (
          <GoogleCalendarLogo className="recurring-meeting-item-logo--google ml-auto" />
        )}
        {!isTeammateType && source === RecurringMeetingSource.office365 && (
          <Outlook365Logo className="recurring-meeting-item-logo--outline365 m-1 ml-auto" />
        )}
        {isTeammateType && <Avatar avatar={teammateAvatar} classNames="m-1 ml-auto" height={34} />}
      </div>
      <AdditionalOptionsSection
        isTeammate={type === WorkspaceType.isTeammate}
        uuid={uuid}
        isExpanded={selected}
        inviteAttendees={inviteAttendees}
        personalMessage={personalMessage}
        onParamsForCreateWorkspaceUpdate={onParamsForCreateWorkspaceUpdate}
      />
    </div>
  );
}

export default RecurringMeetingItem;
